import { Button } from '@finn/design-system/atoms/button';
import { useIsScrolledUp } from '@finn/platform-modules';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { cn } from '@finn/ui-utils';
import { Fragment, ReactNode, useCallback, useContext } from 'react';

import { useDiscoveryConfig } from '../../core/data/MetadataProvider';
import { GenericVehicleDetails } from '../../core/types';
import { useInitializeConfigureStore } from '../../pdp-management';
import { VehicleContext } from '../../product-cards';
import { PartialCartInfo } from '../../product-pricing';
import { ClickableVehiclesBenefits } from './ClickableVehiclesBenefits';
import { ComingSoonSection } from './ComingSoonSection';
import { ImageGallery } from './ImageGallery/ImageGallery';
import { SubscriptionPrice } from './SubscriptionPrice';
import { TrustPilotRating, TrustpilotStyle } from './TrustPilotRating';
import { VehicleMainDetails } from './VehicleMainDetails';

export const allowedAccordingToPolicy = (
  policy: ReturnType<typeof useDiscoveryConfig>['carPolicy'],
  vehicle: GenericVehicleDetails
) => {
  if (!policy) {
    return true;
  }

  const violatesBrandPolicy =
    policy?.brands?.length && !policy.brands.includes(vehicle.brand.id);
  const violatesFuelTypePolicy =
    policy?.fuelTypes?.length && !policy.fuelTypes.includes(vehicle.fuel);
  const violatesCarTypePolicy =
    policy?.carTypes?.length && !policy.carTypes.includes(vehicle.cartype);

  const banned =
    violatesBrandPolicy || violatesFuelTypePolicy || violatesCarTypePolicy;

  return !banned;
};

export const ProductDetailsB = <T,>({
  pageData,
  onAddToCart,
  triggerDevModal,
  renderPromoSection,
  closedUserGroupId,
  cartInfo,
  onCheckout,
  partnerVoucherBanner,
}: {
  onAddToCart: () => void;
  isLoading?: boolean;
  pageData: T;
  hideBreadCrumbs?: boolean;
  triggerDevModal?: () => void;
  renderPromoSection?: () => ReactNode;
  closedUserGroupId?: string;
  cartInfo?: PartialCartInfo;
  priceAreaChildren?: ReactNode;
  onCheckout?: ({
    id,
    term,
    kilometerPackage,
    downPaymentAmount,
  }: {
    id: any;
    term: any;
    kilometerPackage: any;
    downPaymentAmount: any;
  }) => void;
  partnerVoucherBanner?: ReactNode;
}) => {
  const [targetRef] = useIsScrolledUp<HTMLDivElement>();
  const { vehicle, groupedProducts } = useContext(VehicleContext);

  const { carPolicy } = useDiscoveryConfig();
  const isAvailable =
    !!vehicle.availability && allowedAccordingToPolicy(carPolicy, vehicle);
  const pictures = vehicle.pictures.map((pic) => pic?.url).filter(Boolean);
  const disclaimerText = (pageData as any).metadata.content
    ?.bmw_rims_disclaimer;

  useInitializeConfigureStore(vehicle, {
    onCheckout,
  });

  const handleChooseVehicle = useCallback(() => {
    const groupComparison = document.getElementById('group-comparison');

    interactionTrackingEvent(TrackingEventName.CTA_CLICKED, {
      location: 'main',
      configsCount: groupedProducts?.length,
    });

    if (groupComparison) {
      groupComparison.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const keyData = [
    vehicle.model_year,
    vehicle.cartype,
    `Sitze ${vehicle.seats}`,
    `Türen ${vehicle.doors}`,
  ];

  return (
    <>
      <div className="container">
        {partnerVoucherBanner}
        <section className="m-auto flex flex-wrap">
          <div
            className={cn(
              'max-w-full grow-0 basis-full md:pr-10',
              `md:max-w-[calc(100%*2/3)] md:basis-[calc(100%*2/3)]`
            )}
          >
            {renderPromoSection?.()}
            <ImageGallery
              imageUrls={pictures}
              disclaimerText={disclaimerText}
            />
          </div>
          <div
            className={cn(
              'relative m-auto max-w-full grow pt-6 sm:max-w-full sm:basis-full md:m-0 md:pt-0',
              `md:max-w-[calc(100%/3)] md:basis-[calc(100%/3)]`
            )}
          >
            <div className="sticky top-2">
              <div className="flex flex-col gap-6">
                <VehicleMainDetails
                  vehicle={vehicle}
                  handleMoreInformationClick={() => {}}
                  closedUserGroupId={closedUserGroupId}
                  cartInfo={cartInfo}
                  triggerDevModal={triggerDevModal}
                  isAdvancedGrouping
                />
                <div className="flex items-center gap-1">
                  {keyData.map((data) => (
                    <Fragment key={data}>
                      <div className="body-14-light">{data}</div>
                      <div className="size-0.5 rounded bg-pewter" />
                    </Fragment>
                  ))}
                </div>
                {isAvailable && (
                  <>
                    <div className="flex flex-col gap-4" data-cy="price-area">
                      <SubscriptionPrice
                        closedUserGroupId={closedUserGroupId}
                        cartInfo={cartInfo}
                        vehicle={vehicle}
                      />
                      <Button className="w-full" onClick={handleChooseVehicle}>
                        Wähle ein Auto
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <div ref={targetRef} />

              {!isAvailable && (
                <div className="mt-6 flex flex-col" data-cy="price-area">
                  <SubscriptionPrice
                    closedUserGroupId={closedUserGroupId}
                    cartInfo={cartInfo}
                  />
                  <ComingSoonSection onAddToCart={onAddToCart} />
                </div>
              )}

              <TrustPilotRating
                style={TrustpilotStyle.PDP}
                desktopClassName={'left-[-60px] mb-2 mt-8 max-w-fit'}
                mobileClassName={'left-[-60px] mb-2 mt-8 max-w-fit'}
                mobileHeight="20px"
                desktopHeight="20px"
              />

              <ClickableVehiclesBenefits />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
